import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileAudio, Loader2, Upload, X } from 'lucide-react';
import { RiepilogoAudio } from '../../services/verbaliAudioService';
import { caricaFileAudio, DIMENSIONE_PEZZO } from '../../services/verbaliCaricamento';
import VerbaleConsenso from './VerbaleConsenso';
import { FORMATI_ACCETTATI, TESTO_FORMATI } from './testiAudio';

interface VerbaleCaricaAudioProps {
  eventId: string;
  /** La pagina ha già mostrato il promemoria del consenso: non si richiede. */
  consensoGiaDato: boolean;
  onCaricato: (audio: RiepilogoAudio) => void;
  /** Il promemoria è stato confermato qui (la pagina può ricordarlo). */
  onConsensoDato?: () => void;
}

/** Per la testata (Task 14): «Carica audio» apre la scelta del file di questo riquadro. */
export interface VerbaleCaricaAudioHandle {
  scegliFile: () => void;
}

export const TESTO_CARICAMENTO_INTERROTTO =
  'Caricamento interrotto. Scegli di nuovo lo stesso file per riprendere da dove si è fermato.';

interface Avanzamento {
  nome: string;
  dimensione: number;
  inviati: number;
  totale: number;
  ripreso: boolean;
}

type Esito = { tipo: 'ok' | 'info' | 'errore'; testo: string } | null;

const mb = (byte: number) => `${(byte / (1024 * 1024)).toFixed(byte >= 100 * 1024 * 1024 ? 0 : 1).replace('.', ',')} MB`;

/** Caricamento di un file audio già registrato: scelta, consenso, avanzamento, «Interrompi», ripresa. */
const VerbaleCaricaAudio = forwardRef<VerbaleCaricaAudioHandle, VerbaleCaricaAudioProps>(({
  eventId, consensoGiaDato, onCaricato, onConsensoDato,
}, ref) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const vivoRef = useRef(true);
  const [consensoQui, setConsensoQui] = useState(false);
  const [inAttesaConsenso, setInAttesaConsenso] = useState<File | null>(null);
  const [avanzamento, setAvanzamento] = useState<Avanzamento | null>(null);
  const [esito, setEsito] = useState<Esito>(null);

  const inCorso = !!avanzamento;

  useImperativeHandle(ref, () => ({ scegliFile: () => inputRef.current?.click() }), []);

  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  // Uscendo durante il caricamento il browser chiede conferma (riprenderebbe da dove si è fermato).
  useEffect(() => {
    if (!inCorso) return undefined;
    const primaDiUscire = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', primaDiUscire);
    return () => window.removeEventListener('beforeunload', primaDiUscire);
  }, [inCorso]);

  const carica = useCallback(async (file: File) => {
    const controller = new AbortController();
    controllerRef.current = controller;
    setEsito(null);
    setAvanzamento({ nome: file.name, dimensione: file.size, inviati: 0, totale: Math.max(1, Math.ceil(file.size / DIMENSIONE_PEZZO)), ripreso: false });
    let primo = true;
    try {
      const audio = await caricaFileAudio({
        eventId,
        file,
        avvisoConfermato: true,
        segnale: controller.signal,
        onAvanzamento: (inviati, totale) => {
          if (!vivoRef.current) return;
          const ripreso = primo && inviati > 0;
          primo = false;
          setAvanzamento((a) => (a ? { ...a, inviati, totale, ripreso: a.ripreso || ripreso } : a));
        },
      });
      if (!vivoRef.current) return;
      setEsito({ tipo: 'ok', testo: `${file.name}: caricato, la trascrizione parte a breve` });
      onCaricato(audio);
    } catch (err: any) {
      if (!vivoRef.current) return;
      if (err?.name === 'AbortError' || controller.signal.aborted) {
        setEsito({ tipo: 'info', testo: TESTO_CARICAMENTO_INTERROTTO });
      } else {
        setEsito({ tipo: 'errore', testo: (typeof err?.message === 'string' && err.message) || 'Caricamento non riuscito, riprova' });
      }
    } finally {
      if (controllerRef.current === controller) controllerRef.current = null;
      if (vivoRef.current) setAvanzamento(null);
    }
  }, [eventId, onCaricato]);

  const scelto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    // Così riscegliendo lo stesso file l'evento riparte (e il caricamento riprende).
    e.target.value = '';
    if (!file || inCorso) return;
    if (consensoGiaDato || consensoQui) {
      carica(file);
    } else {
      setInAttesaConsenso(file);
    }
  };

  const pct = avanzamento ? Math.min(100, Math.round((avanzamento.inviati / avanzamento.totale) * 100)) : 0;
  const inviatiByte = avanzamento ? Math.min(avanzamento.dimensione, avanzamento.inviati * DIMENSIONE_PEZZO) : 0;

  return (
    <section aria-label="Carica un file audio" className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center gap-2">
        <Upload className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Carica un file audio</span>
      </div>
      <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
        Una registrazione fatta con il telefono o un registratore: {TESTO_FORMATI}.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept={FORMATI_ACCETTATI}
        onChange={scelto}
        className="hidden"
        aria-label="File audio da caricare"
        data-testid="file-audio"
      />

      {avanzamento ? (
        <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3 dark:border-gray-700 dark:bg-gray-900/30">
          <div className="flex items-center gap-2">
            <FileAudio className="h-4 w-4 flex-shrink-0 text-slate-500 dark:text-gray-400" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 dark:text-gray-100" title={avanzamento.nome}>
              {avanzamento.nome}
            </span>
            <span className="text-xs font-semibold tabular-nums text-slate-600 dark:text-gray-300">{pct}%</span>
          </div>
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-gray-700"
            role="progressbar"
            aria-label="Avanzamento del caricamento"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
          >
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-gray-400">
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              {mb(inviatiByte)} di {mb(avanzamento.dimensione)}
              {avanzamento.ripreso ? ' · ripreso da dove si era fermato' : ''}
            </span>
            <button
              type="button"
              onClick={() => controllerRef.current?.abort()}
              className="ml-auto inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <X className="h-3 w-3" aria-hidden="true" />
              Interrompi
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500 dark:bg-gray-800 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          Scegli il file
        </button>
      )}

      {esito && (
        <p
          role={esito.tipo === 'errore' ? 'alert' : 'status'}
          className={`mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
            esito.tipo === 'errore'
              ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
              : esito.tipo === 'info'
                ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
                : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-200'
          }`}
        >
          {esito.tipo === 'ok'
            ? <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            : <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />}
          <span>{esito.testo}</span>
        </p>
      )}

      <VerbaleConsenso
        titolo="Prima di caricare l'audio"
        aperto={!!inAttesaConsenso}
        onConferma={() => {
          const file = inAttesaConsenso;
          setInAttesaConsenso(null);
          setConsensoQui(true);
          onConsensoDato?.();
          if (file) carica(file);
        }}
        onAnnulla={() => setInAttesaConsenso(null)}
      />
    </section>
  );
});

VerbaleCaricaAudio.displayName = 'VerbaleCaricaAudio';

export default VerbaleCaricaAudio;
